import { createSlice } from '@reduxjs/toolkit';
import _orderBy from 'lodash/orderBy';

import { ARCADE_STRUCTURES } from 'constants/common';

import { applyBulkLessonStatusToCourseLikeState } from 'store/helpers/applyBulkLessonStatusToCourseLikeState';
import { removeLessonById, updateLessonStatusById } from 'store/lesson/actions';

import {
  getArcadeById,
  updateArcadeById,
  createArcadeModule,
  updateArcadeModuleById,
  removeArcadeModuleById,
  reorderArcadeLessons,
  reorderArcadeModules,
  updateArcadeStatus,
} from './actions';

const arcadeSlice = createSlice({
  name: 'currentArcade',
  initialState: null,
  reducers: {},
  extraReducers: (builder: any) => {
    builder.addCase(getArcadeById.fulfilled, (_: any, action: any) => {
      return action.payload;
    });

    builder.addCase(updateArcadeById.fulfilled, (state: any, action: any) => {
      return {
        ...state,
        ...action.payload,
      };
    });

    builder.addCase(createArcadeModule.fulfilled, (state: any, action: any) => {
      return {
        ...state,
        modules: [...(state.modules || []), { ...action.payload, lessons: [] }],
      };
    });

    builder.addCase(
      updateArcadeModuleById.fulfilled,
      (state: any, action: any) => {
        return {
          ...state,
          modules: state.modules.map((module: any) => {
            if (module.id === action.payload.id) {
              return {
                ...module,
                ...action.payload,
              };
            }

            return module;
          }),
        };
      },
    );

    builder.addCase(
      removeArcadeModuleById.fulfilled,
      (state: any, action: any) => {
        return {
          ...state,
          modules: state.modules.filter(
            (module: any) => module.id !== action.payload.id,
          ),
        };
      },
    );

    builder.addCase(updateArcadeStatus.fulfilled, (state: any, action: any) => {
      const { bulkLessonStatus, ...coursePatch } = action.payload;

      return applyBulkLessonStatusToCourseLikeState(
        state,
        coursePatch,
        bulkLessonStatus,
      );
    });

    builder.addCase(removeLessonById.fulfilled, (state: any, action: any) => {
      if (!state || !state.structure) {
        return state;
      }

      if (state.structure === ARCADE_STRUCTURES.MODULES) {
        return {
          ...state,
          modules: state.modules.map((module: any) => ({
            ...module,
            lessons: module.lessons.filter(
              (lesson: any) => lesson.id !== action.payload.id,
            ),
          })),
        };
      } else {
        return {
          ...state,
          lessons: state.lessons.filter(
            (lesson: any) => lesson.id !== action.payload.id,
          ),
        };
      }
    });

    builder.addCase(
      updateLessonStatusById.fulfilled,
      (state: any, action: any) => {
        if (!state || !state.structure) {
          return state;
        }

        if (state.structure === ARCADE_STRUCTURES.MODULES) {
          return {
            ...state,
            modules: state.modules.map((module: any) => ({
              ...module,
              lessons: module.lessons.map((lesson: any) => {
                if (lesson.id === action.payload.id) {
                  return {
                    ...lesson,
                    status: action.payload.status,
                  };
                }

                return lesson;
              }),
            })),
          };
        } else {
          return {
            ...state,
            lessons: state.lessons.map((lesson: any) => {
              if (lesson.id === action.payload.id) {
                return {
                  ...lesson,
                  status: action.payload.status,
                };
              }

              return lesson;
            }),
          };
        }
      },
    );

    builder.addCase(
      reorderArcadeLessons.fulfilled,
      (state: any, action: any) => {
        const { moduleId, lessons } = action.payload;

        return {
          ...state,
          modules: state.modules.map((module: any) => {
            if (module.id === moduleId) {
              return {
                ...module,
                lessons,
              };
            }

            return module;
          }),
        };
      },
    );

    builder.addCase(
      reorderArcadeModules.fulfilled,
      (state: any, action: any) => {
        return {
          ...state,
          modules: _orderBy(
            state.modules.map((currentModule: any) => {
              const nextModule = action.payload.find(
                (module: any) => module.id === currentModule.id,
              );

              return {
                ...nextModule,
                lessons: currentModule.lessons,
              };
            }),
            ['order'],
            ['asc'],
          ),
        };
      },
    );
  },
});

export default arcadeSlice.reducer;
